import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAppStore, type SheetSnap } from '../state/store';
import { useIsDesktop } from '../state/useMedia';

const FALLBACK_PEEK_PX = 176;
const MIN_PEEK_PX = 120;
const MAX_PEEK_FRAC = 0.45;

/**
 * Contenitore dei controlli: su desktop un pannello laterale, su mobile un
 * bottom sheet trascinabile con tre posizioni (peek / metà / pieno), come
 * nelle app di mappe. L'altezza corrente è esposta in --sheet-h così mappa,
 * bottoni flottanti e toast si adattano.
 *
 * L'altezza della posizione «peek» è misurata sul primo elemento con
 * l'attributo data-peek (es. riepilogo + bottone «Avvia»), così ciò che
 * conta resta sempre visibile sopra la mappa.
 */
export function Sheet({ children }: { children: ReactNode }) {
  const desktop = useIsDesktop();
  const [snap, setSnap] = useState<SheetSnap>('half');
  const [vh, setVh] = useState(() => window.innerHeight);
  const [dragH, setDragH] = useState<number | null>(null);
  const [peekH, setPeekH] = useState(FALLBACK_PEEK_PX);
  const drag = useRef<{ startY: number; startH: number; startT: number } | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const sheetRequest = useAppStore((s) => s.sheetRequest);
  const requestSheet = useAppStore((s) => s.requestSheet);

  useEffect(() => {
    if (sheetRequest) {
      setSnap(sheetRequest);
      requestSheet(null);
    }
  }, [sheetRequest, requestSheet]);

  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // misura il blocco [data-peek] per calcolare l'altezza della posizione «peek»
  useLayoutEffect(() => {
    const content = contentRef.current;
    const sheet = sheetRef.current;
    if (!content || !sheet || desktop) return;
    const measure = () => {
      const el = content.querySelector<HTMLElement>('[data-peek]');
      if (!el) {
        setPeekH(FALLBACK_PEEK_PX);
        return;
      }
      const bottomPad = parseFloat(getComputedStyle(content).paddingBottom) || 0;
      const h = el.getBoundingClientRect().bottom - sheet.getBoundingClientRect().top + bottomPad;
      setPeekH(Math.round(Math.max(MIN_PEEK_PX, Math.min(window.innerHeight * MAX_PEEK_FRAC, h))));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(content);
    const el = content.querySelector('[data-peek]');
    if (el) ro.observe(el);
    return () => ro.disconnect();
  }, [desktop, vh, children]);

  const heights = useMemo<Record<SheetSnap, number>>(
    () => ({ peek: peekH, half: Math.round(vh * 0.5), full: Math.round(vh * 0.92) }),
    [peekH, vh],
  );
  const fullH = heights.full;
  const current = dragH ?? heights[snap];

  useEffect(() => {
    document.documentElement.style.setProperty('--sheet-h', desktop ? '0px' : `${current}px`);
  }, [current, desktop]);

  // stato dello sheet esposto al CSS (nasconde scheda e bottoni quando è a schermo pieno)
  useEffect(() => {
    document.documentElement.dataset.sheet = desktop ? 'panel' : dragH !== null ? 'dragging' : snap;
  }, [desktop, snap, dragH]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      drag.current = { startY: e.clientY, startH: current, startT: performance.now() };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [current],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!drag.current) return;
      const h = Math.max(
        MIN_PEEK_PX,
        Math.min(fullH, drag.current.startH + (drag.current.startY - e.clientY)),
      );
      setDragH(h);
    },
    [fullH],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const d = drag.current;
      drag.current = null;
      if (!d) return;
      const dy = d.startY - e.clientY; // positivo = trascinato verso l'alto
      const dt = Math.max(1, performance.now() - d.startT);
      const velocity = dy / dt; // px/ms
      const order: SheetSnap[] = ['peek', 'half', 'full'];
      let next: SheetSnap;
      if (Math.abs(dy) < 6) {
        // tap sul grabber: alterna metà / pieno (da peek si apre a metà)
        next = snap === 'half' ? 'full' : 'half';
      } else {
        // posizione proiettata: dove arriverebbe il gesto continuando con la
        // velocità attuale, così un «flick» deciso salta anche due posizioni
        const projected = d.startH + dy + velocity * 200;
        next = order.reduce((best, s) =>
          Math.abs(heights[s] - projected) < Math.abs(heights[best] - projected) ? s : best,
        );
      }
      setSnap(next);
      setDragH(null);
    },
    [snap, heights],
  );

  if (desktop) {
    return <aside className="panel material">{children}</aside>;
  }

  const scrollable = snap !== 'peek' && dragH === null;
  return (
    <div
      ref={sheetRef}
      className={`sheet material${dragH !== null ? ' sheet--dragging' : ''}`}
      style={{ height: fullH, transform: `translateY(${fullH - current}px)` }}
    >
      <div
        className="sheet__grabber"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="button"
        tabIndex={0}
        aria-label="Trascina per ridimensionare il pannello"
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') setSnap(snap === 'peek' ? 'half' : 'full');
          if (e.key === 'ArrowDown') setSnap(snap === 'full' ? 'half' : 'peek');
        }}
      >
        <span />
      </div>
      <div
        ref={contentRef}
        className={`sheet__content${scrollable ? ' sheet__content--scroll' : ''}`}
      >
        {children}
      </div>
    </div>
  );
}
